import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { buyerContactService } from '@/services/buyerContactService';
import { useToast } from '@/hooks/use-toast';
import { Mail, Phone, Copy, ExternalLink } from 'lucide-react';

interface ContactBuyerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  buyerId: string;
  buyerName: string;
  buyerContactInfo?: {
    email?: string | null;
    phone_number?: string | null;
  };
}

export const ContactBuyerDialog = ({ open, onOpenChange, buyerId, buyerName, buyerContactInfo }: ContactBuyerDialogProps) => {
  const [buyerInfo, setBuyerInfo] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  
  const { toast } = useToast();

  useEffect(() => {
    if (open && buyerId) {
      // Use provided contact info if available, otherwise fetch
      if (buyerContactInfo && (buyerContactInfo.email || buyerContactInfo.phone_number)) {
        setBuyerInfo(buyerContactInfo);
      } else {
        fetchBuyerInfo();
      }
      setEmailSubject(`Investment Opportunity from ${buyerName}`);
      setEmailMessage(`Hi ${buyerName},\n\nI have some investment properties that might interest you based on your preferences.\n\nWould you like to discuss these opportunities?\n\nBest regards`);
    }
  }, [open, buyerId, buyerName, buyerContactInfo]);

  const fetchBuyerInfo = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await buyerContactService.getBuyerContactInfo(buyerId);
      if (error) {
        toast({
          title: "Error",
          description: error,
          variant: "destructive"
        });
      } else {
        setBuyerInfo(data);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load buyer contact information",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailSubject.trim() || !emailMessage.trim()) {
      toast({
        title: "Error",
        description: "Please fill in both subject and message",
        variant: "destructive"
      });
      return;
    }

    setIsSending(true);
    try {
      const { success, error } = await buyerContactService.sendEmailToBuyer(
        buyerId,
        emailSubject.trim(),
        emailMessage.trim()
      );

      if (success) {
        toast({
          title: "Email Sent",
          description: `Your message has been sent to ${buyerName}`,
        });
        onOpenChange(false);
      } else {
        toast({
          title: "Error",
          description: error || "Failed to send email",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setIsSending(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied",
      description: `${label} copied to clipboard`,
    });
  };

  const openEmailClient = () => {
    const subject = encodeURIComponent(emailSubject);
    const body = encodeURIComponent(emailMessage);
    const email = buyerInfo?.email;
    
    if (!email) {
      toast({
        title: "Error",
        description: "No email address available for this buyer",
        variant: "destructive"
      });
      return;
    }
    
    window.open(`mailto:${email}?subject=${subject}&body=${body}`);
    toast({
      title: "Email Client Opened",
      description: "Your email application should open shortly",
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Contact {buyerName}</DialogTitle>
        </DialogHeader>
        
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : buyerInfo ? (
          <div className="space-y-6">
            {/* Contact Information */}
            <div className="space-y-3">
              <h4 className="font-medium">Contact Information</h4>
              
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <span className="text-sm">{buyerInfo.email}</span>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(buyerInfo.email, 'Email')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={openEmailClient}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  <span className="text-sm">{buyerInfo.phone_number}</span>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(buyerInfo.phone_number, 'Phone')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(`tel:${buyerInfo.phone_number}`)}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Email Form */}
            <div className="space-y-4">
              <h4 className="font-medium">Send Email</h4>
              
              <div className="space-y-2">
                <Label>Subject</Label>
                <Input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Email subject"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Message</Label>
                <Textarea
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  placeholder="Your message"
                  rows={6}
                />
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={openEmailClient} className="flex-1">
                Open Email Client
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-muted-foreground">Contact information not available. Buyer must view your properties first to enable contact.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};